import { Tooltip, Typography } from "@mui/material";
import { FaLocationDot } from "react-icons/fa6";
import { Link } from "react-router-dom";

import { TCourse } from "../../../@types";
import useScreenSize from "../../../hooks/useScreenSize";
import { USDFormatter } from "../../../utils";

import "./course_list.scss";

export type TSplittedCourses = { category: string; courses: TCourse[] }[];

export type TCourseListProps = {
	splittedCourses: TSplittedCourses;
	className?: string;
};

export function CourseList(props: TCourseListProps) {
	const { width } = useScreenSize();
	const isMobile = width <= 768;

	let gap = 32;
	if (isMobile) {
		gap = 16;
	}

	let itemWidth = 350;
	if (
		Math.floor((width * 0.9 + gap) / (300 + gap)) >
		(width * 0.9 + gap) / (350 + gap)
	) {
		itemWidth =
			(width * 0.9 + gap) / Math.floor((width * 0.9 + gap) / (300 + gap)) - gap;
	}
	return (
		<div>
			{props.splittedCourses.map((section) => {
				return (
					<div key={section.category} className={props.className}>
						<Typography className="small-title-text bold">
							{`${section.category} (${section.courses.length})`}
						</Typography>
						<div
							className="courses-container item-separator-tiny"
							style={{ gap: `${gap}px` }}>
							{section.courses.map((course) => {
								const totalLessons = course.lesson.length;
								let totalTeachingTime = 0;
								let totalFlightTime = 0;
								const airplanes = new Set<string>();
								for (let i = 0; i < totalLessons; i++) {
									totalTeachingTime +=
										(course.lesson[i].teachingTime ?? 0) / 60;
									totalFlightTime += (course.lesson[i].flightTime ?? 0) / 60;
									if (course.lesson[i].airplane) {
										airplanes.add(course.lesson[i].airplane);
									}
								}
								const airplaneString = Array.from(airplanes).join(", ");

								let totalTeachingTimeText = "";
								if (totalTeachingTime === 1) {
									totalTeachingTimeText = `${totalTeachingTime.toFixed(2)} hr knowledge`;
								} else {
									totalTeachingTimeText = `${totalTeachingTime.toFixed(2)} hrs knowledge`;
								}
								let totalFlightTimeText = "";
								if (totalFlightTime === 1) {
									totalFlightTimeText = `${totalFlightTime.toFixed(2)} hr skill`;
								} else {
									totalFlightTimeText = `${totalFlightTime.toFixed(2)} hrs skill`;
								}
								if (airplaneString) {
									totalFlightTimeText = `${totalFlightTimeText} (${airplaneString})`;
								}

								let trainingFrom = "";
								if (course.teacher?.firstName) {
									trainingFrom = `Training from ${course.teacher.firstName}`;
									if (course.teacher.profile?.nickName) {
										trainingFrom = `${trainingFrom} (${course.teacher.profile.nickName})`;
									}
								}

								let renderedElement = null;
								let document = null;
								if (course.courseDocuments?.length > 0) {
									document = course.courseDocuments[0];
								}
								if (document && document.documentType === "COURSE_IMG") {
									renderedElement = (
										<img
											className="course-media"
											src={document.thumbnail || document.url}
											alt={`${document.id}`}
										/>
									);
								} else if (document && document.documentType === "TRAILER") {
									renderedElement = (
										<video
											className="course-media"
											autoPlay
											muted
											loop
											playsInline>
											<source src={document.url} type="video/mp4" />
										</video>
									);
								}

								return (
									<Link
										key={course.id}
										to={`/services/${course.courseSlug}`}
										style={{
											textDecoration: "unset",
											color: "unset",
											width: `${itemWidth}px`,
										}}
										className="course-item-container">
										{renderedElement}
										{renderedElement && (
											<div className="course-name-container">
												<Typography className="normal-text bold">
													{course.name}
												</Typography>
											</div>
										)}
										{!renderedElement && (
											<div className="no-media-course-name-container">
												<Typography className="normal-text bold">
													{course.name}
												</Typography>
											</div>
										)}
										<div className="course-info-container">
											<div style={{ flex: 1 }}>
												{course.teacher && (
													<Link
														to={`/profile/${course.teacher.profile?.profileSlug ?? ""}`}
														className="small-text black-text underline-on-hover-link">
														{trainingFrom}
													</Link>
												)}
												<div className="time-container">
													<div style={{ flex: 1 }}>
														<Typography className="small-text">
															{totalTeachingTimeText}
														</Typography>
														{totalFlightTime > 0 && (
															<Typography className="small-text">
																{totalFlightTimeText}
															</Typography>
														)}
													</div>
													<Typography className="small-text bold">
														{USDFormatter.format(course.price)}
													</Typography>
												</div>
											</div>
											{course.location && (
												<>
													<div className="separator" />
													<div className="location-container">
														<div>
															<FaLocationDot className="normal-icon" />
														</div>
														<Tooltip title="Click to show in Google Maps">
															<Link
																to={`https://www.google.com/maps/search/?api=1&query=${course.latitude},${course.longitude}`}
																className="small-text location"
																target="_blank">
																{course.location}
															</Link>
														</Tooltip>
													</div>
												</>
											)}
										</div>
									</Link>
								);
							})}
						</div>
					</div>
				);
			})}
		</div>
	);
}
